import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { COLORS } from '../../globalStyle/colors';
import { css } from '@emotion/core';
import { Z_INDEX } from '../../globalStyle/zIndex';
import { motion } from 'framer-motion';
import { useKeyListener } from '../../core/hooks/useKeyListener';
import { onPhone, onMobileDevice } from '../../globalStyle/mediaQueries';


const styles = {
  modalBg: css`
    position: fixed;
    z-index: ${Z_INDEX.MODAL};
    
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    
    display: flex;
    align-items: center;
    justify-content: center;
    
    background-color: rgba(0, 0, 0, 0.8);
    cursor: pointer;
    opacity: 0;
  `,
  modal:   css`
    cursor: default;
    overflow-x: hidden;
    overflow-y: auto;
    background-color: ${COLORS.LIGHTER_GREY};
    opacity: 0;
    
    max-height: calc(100vh - 32px);
    max-width: calc(100vw - 32px);
    min-width: 550px;
    min-height: 550px;
    
    transition: all .5s ease;
    
    ${onMobileDevice} {
      min-width: 320px;
    } 

    ${onPhone} {
      max-width: 100vw;
      max-height: 100vh;
      min-width: 0;
      min-height: 0;
      width: 100vw;
      height: 100vh;
    }
  `,
};


type Props = {
  dismissable?: boolean,

  onDismiss?: () => void,

  dismissOnClickOutside?: boolean;
  dismissOnEsc?: boolean;

  children: React.ReactNode | React.ReactNode[];
  className?: string;
};


/**
 * Modal: a component that renders its children as a modal with a dark background and intuitive close functionality.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const Modal: React.FC<Props> = props => {
  const onClickToDismissModal = React.useCallback(
    () => {
      if (props.dismissOnClickOutside && props.dismissable) {
        props.onDismiss?.();
      }
    },
    [props.onDismiss, props.dismissable, props.dismissOnClickOutside],
  );

  const onKeyUp = React.useCallback(
    (event: KeyboardEvent) => {
      if (props.dismissable && props.dismissOnEsc && event.key === 'Escape') {
        props.onDismiss?.();
      }
    },
    [props.onDismiss, props.dismissable, props.dismissOnEsc],
  );

  const stopPropagation = React.useCallback(
    (event: React.MouseEvent) => event && event.stopPropagation(),
    [],
  );

  useKeyListener(onKeyUp);

  return ReactDOM.createPortal(
    <motion.div
      key='modal-bg'
      aria-modal='true'
      onClick={onClickToDismissModal}
      css={styles.modalBg}
      animate={{ opacity: [0, 1] }}
      transition={{ duration: .4 }}
    >
      <motion.div
        key='modal-content'
        onClick={stopPropagation}
        animate={{ scale: [.9, 1], opacity: [0, 1] }}
        transition={{ duration: .25 }}
        css={styles.modal}
        className={props.className}
      >
        {
          props.children
        }
      </motion.div>
    </motion.div>,
    document.body,
  );
};

Modal.defaultProps = {
  dismissable:           true,
  dismissOnClickOutside: true,
  dismissOnEsc:          true,
};

export default Modal;
